/**
 * Client-side helper for the beta "create a test game" flow. Wraps
 * POST /api/games with the host's chosen config (map, AI personalities
 * for 2v2-ai) and returns the id the server picked. Called from the
 * BetaConfigModal's CREATE & SHARE button once the host has tapped
 * their settings.
 *
 * Joiners never hit this endpoint — they land in JoinScene via the
 * shared URL.
 */

import type { PersonalityId } from '../game/ai/personality.js';
import { apiBaseUrl } from '../platform/index.js';
import { getAuth } from './auth.js';

export type BetaGameMode = 'ffa' | '2v2-ai';

export interface BetaGameConfig {
  /** Map id, e.g. 'ring' | 'slash' | 'terra'. */
  readonly mapId: string;
  /**
   * AI personalities by AI seat order (only meaningful for 2v2-ai).
   * Length is expected to be 2; extras are ignored, missing entries
   * fall back to server defaults.
   */
  readonly aiPersonalities?: readonly PersonalityId[];
  /** Opt the lobby into the public-games browser. Omit or false for
   *  invite-link-only (current default behavior). */
  readonly isPublic?: boolean;
  /** Auto-start threshold the host chose. FFA: 2, 3, or 4. 2v2-ai is
   *  fixed at 2 (the server rejects anything else). Omit to let the
   *  server pick the mode default (FFA → 4, 2v2-ai → 2). */
  readonly targetSeats?: number;
}

export interface CreatedBetaGame {
  readonly id: string;
  readonly mode: BetaGameMode;
  readonly isPublic: boolean;
  readonly targetSeats: number;
}

export async function createBetaGame(
  mode: BetaGameMode,
  config: BetaGameConfig
): Promise<CreatedBetaGame> {
  const payload: Record<string, unknown> = {
    mapId: config.mapId,
    mode,
  };
  if (mode === '2v2-ai' && config.aiPersonalities) {
    payload['aiPersonalities'] = [...config.aiPersonalities];
  }
  // Only send isPublic when explicitly true — the server defaults
  // to false, so omitting the field for a private game keeps the
  // payload tidy.
  if (config.isPublic === true) {
    payload['isPublic'] = true;
  }
  if (typeof config.targetSeats === 'number') {
    payload['targetSeats'] = config.targetSeats;
  }
  // Authorization header is required post-Slice 1.3. If there's no
  // session, server returns 401 → the throw below surfaces it. UI
  // should gate this call behind the HomeScene auth banner; this
  // helper doesn't try to re-authenticate.
  const token = getAuth().session?.access_token;
  const headers: Record<string, string> = {
    'content-type': 'application/json',
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${apiBaseUrl()}/api/games`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(`create beta game failed: ${JSON.stringify(err)}`);
  }
  const data = (await res.json()) as {
    id: string;
    mode: BetaGameMode;
    isPublic?: boolean;
    targetSeats?: number;
  };
  return {
    id: data.id,
    mode: data.mode,
    isPublic: data.isPublic === true,
    targetSeats:
      typeof data.targetSeats === 'number'
        ? data.targetSeats
        : data.mode === '2v2-ai'
          ? 2
          : 4,
  };
}
